/**
 * Losi Task Assignee
 *
 * Shared constants and helpers for Losi's virtual Spaces task identity.
 *
 * Primary responsibilities:
 * - Define the reserved Losi assignee id and mention labels
 * - Build synthetic profile payloads for assignee and comment surfaces
 *
 * Dependencies:
 * - None
 */

export const LOSI_TASK_ASSIGNEE_ID = "losi"
export const LOSI_TASK_ASSIGNEE_NAME = "Losi"
export const LOSI_TASK_ASSIGNEE_USERNAME = "losi"
export const LOSI_TASK_ASSIGNEE_EMAIL = "losi@system.losi"
export const LOSI_TASK_ASSIGNEE_IMAGE_URL = "/losi-icon.png"
export const LOSI_TASK_COMMENT_CONTENT_TYPE = "markdown"
export const LOSI_TASK_COMMENT_MARKER = "<!-- losi:task-comment -->"
const STRUCTURED_MENTION_REGEX =
  /@?\[([^\]]+)\]\(([^:()\s]+):([^)]+)\)/gi
const HTML_MENTION_ID_REGEX =
  /<mention\b[^>]*(?:user_id|user-id|data-user-id|id)=["']([^"']+)["'][^>]*>/gi
const LOSI_PLATFORM_MENTION_IDS = new Set([
  "losi",
  "platform",
  "losi-assistant",
  "losi-assistant-mention"
])
const LOSI_AUTHORED_TASK_COMMENT_CONTENT_TYPES = new Set([
  "approval_request",
  "user_handoff_request"
])
const LOSI_APPROVAL_REPLY_PREFIXES = [
  "approval approved for the previous losi approval request.",
  "approval rejected for the previous losi approval request.",
  "revision requested for the previous losi approval request."
]

type SyntheticTaskProfile = {
  id?: string | null
  user_id?: string | null
  display_name?: string | null
  username?: string | null
  email?: string | null
  image_url?: string | null
}

function normalizeString(value: unknown) {
  return typeof value === "string" && value.trim().length > 0
    ? value.trim()
    : null
}

// --------------

function normalizeMentionType(value: unknown) {
  return normalizeString(value)?.toLowerCase().replace(/_/g, "-") ?? null
}

// --------------

function parseStructuredMentionTarget(value: string) {
  const match = value.match(/^@?\[[^\]]+\]\(([^:()\s]+):([^)]+)\)$/)

  if (!match) {
    return null
  }

  return {
    type: match[1],
    id: match[2]
  }
}

// --------------

function structuredMentionTargetsLosi(type: unknown, id: unknown) {
  const normalizedType = normalizeMentionType(type)
  const normalizedId = normalizeString(id)?.toLowerCase()

  if (!normalizedType || !normalizedId) {
    return false
  }

  if (
    (normalizedType === "member" || normalizedType === "space-member") &&
    isLosiTaskAssigneeId(normalizedId)
  ) {
    return true
  }

  if (
    normalizedType === "losi" &&
    LOSI_PLATFORM_MENTION_IDS.has(normalizedId)
  ) {
    return true
  }

  if (
    (normalizedType === "platform" || normalizedType === "platform-losi") &&
    LOSI_PLATFORM_MENTION_IDS.has(normalizedId)
  ) {
    return true
  }

  return false
}

// --------------

function isPlainMentionBoundary(value: string | undefined) {
  return !value || !/[\w-]/.test(value)
}

// --------------

function isPlainMentionTerminator(
  value: string | undefined,
  nextValue: string | undefined
) {
  if (!value) {
    return true
  }

  if (/[\s,!?;:)\]}>/]/.test(value)) {
    return true
  }

  return value === "." && (!nextValue || /\s/.test(nextValue))
}

// --------------

function hasPlainLosiMention(content: string) {
  for (const match of content.matchAll(/@losi/gi)) {
    const index = match.index ?? -1
    const afterIndex = index + LOSI_TASK_ASSIGNEE_ID.length + 1

    if (
      index >= 0 &&
      isPlainMentionBoundary(content[index - 1]) &&
      isPlainMentionTerminator(content[afterIndex], content[afterIndex + 1])
    ) {
      return true
    }
  }

  return false
}

// --------------

export function isLosiTaskAssigneeId(value: unknown) {
  return normalizeLosiTaskAssigneeId(value) === LOSI_TASK_ASSIGNEE_ID
}

// --------------

export function normalizeLosiTaskAssigneeId(value: unknown) {
  const normalizedValue = normalizeString(value)

  if (!normalizedValue) {
    return null
  }

  const lowerValue = normalizedValue.toLowerCase()

  if (
    lowerValue === LOSI_TASK_ASSIGNEE_ID ||
    lowerValue === `@${LOSI_TASK_ASSIGNEE_ID}`
  ) {
    return LOSI_TASK_ASSIGNEE_ID
  }

  const mentionTarget = parseStructuredMentionTarget(normalizedValue)
  if (
    mentionTarget &&
    structuredMentionTargetsLosi(mentionTarget.type, mentionTarget.id)
  ) {
    return LOSI_TASK_ASSIGNEE_ID
  }

  const splitIndex = lowerValue.indexOf(":")
  if (splitIndex > 0) {
    const type = lowerValue.slice(0, splitIndex)
    const id = lowerValue.slice(splitIndex + 1)

    if (structuredMentionTargetsLosi(type, id)) {
      return LOSI_TASK_ASSIGNEE_ID
    }
  }

  return null
}

// --------------

export function markLosiTaskCommentContent(content: string) {
  const trimmedContent = content.trim()

  return trimmedContent.startsWith(LOSI_TASK_COMMENT_MARKER)
    ? trimmedContent
    : `${LOSI_TASK_COMMENT_MARKER}\n${trimmedContent}`
}

// --------------

export function stripLosiTaskCommentMarker(content: unknown) {
  if (typeof content !== "string") {
    return ""
  }

  const trimmedStartContent = content.trimStart()

  return trimmedStartContent.startsWith(LOSI_TASK_COMMENT_MARKER)
    ? trimmedStartContent
        .slice(LOSI_TASK_COMMENT_MARKER.length)
        .replace(/^\r?\n/, "")
    : content
}

// --------------

export function isLosiAuthoredTaskComment(comment: {
  user_id?: unknown
  content?: unknown
  content_type?: unknown
}) {
  if (isLosiTaskAssigneeId(comment.user_id)) {
    return true
  }

  const contentType = normalizeString(comment.content_type)

  if (!contentType) {
    return false
  }

  const content = normalizeString(comment.content)

  if (content?.startsWith(LOSI_TASK_COMMENT_MARKER)) {
    return true
  }

  if (LOSI_AUTHORED_TASK_COMMENT_CONTENT_TYPES.has(contentType)) {
    return true
  }

  const normalizedContent = content?.toLowerCase()

  return contentType === "approval_reply" && normalizedContent
    ? LOSI_APPROVAL_REPLY_PREFIXES.some(prefix => normalizedContent.startsWith(prefix))
    : false
}

// --------------

export function isLosiMentionLabel(value: unknown) {
  const normalizedValue = normalizeString(value)?.toLowerCase()

  return (
    normalizedValue === LOSI_TASK_ASSIGNEE_ID ||
    normalizedValue === `@${LOSI_TASK_ASSIGNEE_ID}`
  )
}

// --------------

export function textMentionsLosi(content: string) {
  const normalizedContent = normalizeString(content)

  if (!normalizedContent) {
    return false
  }

  for (const match of normalizedContent.matchAll(STRUCTURED_MENTION_REGEX)) {
    if (structuredMentionTargetsLosi(match[2], match[3])) {
      return true
    }
  }

  for (const match of normalizedContent.matchAll(HTML_MENTION_ID_REGEX)) {
    if (isLosiTaskAssigneeId(match[1])) {
      return true
    }
  }

  return hasPlainLosiMention(normalizedContent)
}

// --------------

export function buildLosiSyntheticTaskProfile(): SyntheticTaskProfile {
  return {
    id: null,
    user_id: LOSI_TASK_ASSIGNEE_ID,
    display_name: LOSI_TASK_ASSIGNEE_NAME,
    username: LOSI_TASK_ASSIGNEE_USERNAME,
    email: LOSI_TASK_ASSIGNEE_EMAIL,
    image_url: LOSI_TASK_ASSIGNEE_IMAGE_URL
  }
}
